import { Component, OnInit, OnDestroy } from "@angular/core";
import { FormControl, FormGroup, FormBuilder } from '@angular/forms';
import { Router, ActivatedRoute } from "@angular/router";
import * as languageLibrary from '../../../services/language'
import moment from 'moment';
import { CdkDragDrop, moveItemInArray, transferArrayItem } from '@angular/cdk/drag-drop';
// services
import { MessageService } from "../../../services/message.service";
import { MasterService } from "../../../services/master.service";
import { StoreService } from '../../../services/store.service'
import { formatPhone } from '../../../utils/format';
import { SEARCH_DEBOUNCE_MILLIS, MIN_SEARCH_CHARS, SCROLL_BOTTOM_OUT } from "src/app/constants/list";
import { SecurityService } from "src/app/services/security.service";
import { Sale } from "src/app/models/sale.model";
import { SaleStatus } from "src/app/models/sale-status.model";
import { ListHeader } from "src/app/models/list-header.model";
import { Lot } from "src/app/models/lot.model";
import { ago } from "src/app/utils/date";
import { Subscription } from "rxjs";

@Component({
    selector: "sales",
    templateUrl: "./sales.component.html",
    styleUrls: ["./sales.component.scss"],
    standalone: false
})

export class SalesComponent implements OnInit, OnDestroy {

    private FILTER_INQUIRY_KEY = 'flt-sls-q'
    private FILTER_LOT_KEY = 'flt-sls-l'
    private FILTER_TYPE_KEY = 'flt-sls-t'
    private FILTER_STATUS_KEY = 'flt-sls-s'
    protected FILTER_SPAN_KEY = 'flt-sls-when'

    protected readonly CASH = 1;
    protected readonly WHOLESALE = 2;
    protected readonly OUTSIDE = 3;
    protected readonly BHPH = 4;
    protected readonly BUYER_ROLE = 1
    protected readonly COBUYER_ROLE = 2
    protected readonly COSIGNER_ROLE = 3
    protected readonly RETAIL_TRANS_TYPE = 'Retail'

    // save the language
    protected language = localStorage.getItem('language') || 'EN'
    
    // set all words
    protected words = languageLibrary.language
    
    protected sales: Sale[] = []

    protected lots: Lot[] = []

    protected statuses: SaleStatus[] = []

    // table headers
    protected headers: ListHeader[] = [
        { name: 'Vehicle', param: 'image', view: true, disabled: false },
        { name: 'Buyer', param: 'buyer', view: true, disabled: false },
        { name: 'Co-Buyer', param: 'cobuyerName', view: false, disabled: false },
        { name: 'Sale Date', param: 'saleDate', view: true, disabled: false },
        { name: 'Stock #', param: 'stockNumber', view: true, disabled: false },
        { name: 'Stage', param: 'saleStage', view: true, disabled: false },
        { name: 'Status', param: 'saleStatus', view: true, disabled: false },
        { name: 'VIN #', param: 'vinNumber', view: false, disabled: false },
        { name: 'Lienholder', param: 'primaryLienholder', view: true, disabled: false },
        { name: 'Sale Financing', param: 'saleFinancingType', view: true, disabled: false },
        { name: 'Exterior Color', param: 'exteriorColor', view: false, disabled: false },
        { name: 'Year', param: 'modelYear', view: false, disabled: false },
        { name: 'Make', param: 'make', view: false, disabled: false },
        { name: 'Model', param: 'model', view: false, disabled: false },
        { name: 'Mileage', param: 'miles', view: false, disabled: false },
        { name: 'Title Condition', param: 'titleCondition', view: false, disabled: false },
        { name: 'Sale Price', param: 'sellingPrice', view: true, disabled: false },
        { name: 'Inspection Expires', param: 'inspectionExpires', view: false, disabled: false },
        { name: 'Has Active Floor Plan', param: 'active', view: true, disabled: false },
        { name: 'Inventory Source', param: 'inventorySource', view: false, disabled: false },
        { name: 'Out of State', param: 'outOfState', view: false, disabled: false },
        { name: 'Phone Number', param: 'phoneNumber', view: true, disabled: false },
        { name: 'Email', param: 'email', view: false, disabled: false },
        { name: 'Address', param: 'address', view: false, disabled: false },
        { name: 'Salesperson', param: 'salesperson', view: true, disabled: false },
        { name: 'Account Owner', param: 'account', view: true, disabled: false },
        { name: 'Lot', param: 'lotId', view: false, disabled: false },

    ]
    
    // define if is loading
    protected loading: boolean = false
        
    // Current page the user is viewing (last loaded).
    protected page = 1

    // The number contacts currently being displayed.
    protected displayed = 0

    // Indicates whether or not there are no more records to load.
    protected exhausted = false

    // Total number of records available
    protected records: number = 0

    // The search form group
    protected form: FormGroup | null = null

    // The current search text to filter the list by.
    private inquiry: string | null = null

    // A debounce timer for search events. Used to wait for 
    // the user to stop typing before we fetch.
    private watcher: ReturnType<typeof setTimeout> | null = null

    // Keep track of how the user wants to sort list.
    private sortables: Object = {'saleDate': 'DESC'}
    
    // define lot selected
    protected lotSelected = 0
    
    // define the object preselected
    protected preselectedObject: Object = {}
    
    //
    protected groupParams = []
    
    //
    protected preselected = {}
    
    //
    protected enableBHPH: boolean = false

    // all cobuyers
    protected cobuyers: any[] = []

    protected userId: Number = null

    protected isQuote: boolean = false

    private subscriptions: Subscription[] = []

    // Determine the path to use based on whether we are viewing a sale or quote list.
    get path (): string { return this.isQuote ? 'quotes' : 'sales' }

    constructor(private route: ActivatedRoute, private ms: MessageService, private master: MasterService, private store: StoreService, private router: Router, protected security: SecurityService, private fb: FormBuilder) {
        this.form = this.fb.group({
            inquiry: new FormControl<string>('', []),
            lot: new FormControl<number>(0, []),
            type: new FormControl<string>('', []),
            status: new FormControl<number>(0, []),
            span: new FormControl<string>('', []),
            from: new FormControl<string>('', []),
            to: new FormControl<string>('', [])
        })
    }

    ngOnInit() {
        this.lotSelected = parseInt(localStorage.getItem('lot')) || parseInt(this.store.lotSelected) || 0
        if (this.lotSelected && !isNaN(this.lotSelected) && (this.lotSelected > 0)) {
            this.form?.get('lot')?.setValue(this.lotSelected, {emitEvent: false})
        }

        const path = this.route.snapshot.url[0].path
        const pattern = new RegExp('quote', 'i')
        this.isQuote = (path && pattern.test(path))

        if (this.isQuote) {
            this.FILTER_INQUIRY_KEY = this.FILTER_INQUIRY_KEY.replace('-sls-', '-quo-')
            this.FILTER_TYPE_KEY = this.FILTER_TYPE_KEY.replace('-sls-', '-quo-')
            this.FILTER_STATUS_KEY = this.FILTER_STATUS_KEY.replace('-sls-', '-quo-')
            this.FILTER_LOT_KEY = this.FILTER_LOT_KEY.replace('-sls-', '-quo-')
            this.FILTER_SPAN_KEY = this.FILTER_SPAN_KEY.replace('-sls-', '-quo-')
        }
    
        this.subscriptions.push(
            this.form.get('inquiry').valueChanges.subscribe(inquiry => {
                if (this.watcher) {
                    clearTimeout(this.watcher)
                    this.watcher = null
                }
        
                if (this.inquiry === inquiry) {
                    return
                }
        
                this.watcher = setTimeout(() => {
                    this.writeFilters()
                    this.inquiry = inquiry
                    this.reset()            
                    this.fetch()
                }, SEARCH_DEBOUNCE_MILLIS)
            })
        )

        this.subscriptions.push(
            this.form.get('lot').valueChanges.subscribe(lot => {
                this.writeFilters()
                this.reset()            
                this.fetch()
            })
        )

        this.subscriptions.push(
            this.form.get('type').valueChanges.subscribe(type => {
                this.writeFilters()
                this.reset()            
                this.fetch()
            })
        )

        this.subscriptions.push(
            this.form.get('status').valueChanges.subscribe(status => {
                this.writeFilters()
                this.reset()            
                this.fetch()
            })
        )

        this.subscriptions.push(
            this.form.get('span').valueChanges.subscribe(span => {
                this.writeFilters()
                this.reset()            
                this.fetch()
            })
        )

        this.populate()
    }

    ngOnDestroy(): void {
        for (const subscription of this.subscriptions) {
            subscription.unsubscribe()
        }

        if (this.watcher !== null) {
            clearTimeout(this.watcher)
            this.watcher = null
        }
    }

    /**
     * Reset the pagination parameters and scroll to the top
    */
    private reset() {
        this.page = 1
        this.exhausted = false
        this.displayed = 0

        const list = document.querySelector('#listing')
        if (!list) {
            return
        }

        list.scrollTo({
            top: 0,
            left: 0,
            behavior: 'auto'
        })
    }

    private populate = () => {
        this.setEnableBHPH()
        this.fetchStatuses()
        this.readFilters()
        this.fetch()
    }

    /**
     * 
     */
    private fetchStatuses() {
        this.master.get(`collections/sales/statuses`, res => {
            if (!res) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] })
                return
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error })
                return
            }

            this.statuses = res.data['Statuses']
        })
    }

    /**
     * 
     */
    private setEnableBHPH() {
        if (!this.lotSelected || (this.lotSelected <= 0)) {
            return
        }

        this.master.get(`lots/${this.lotSelected}/salesSetup`, res => {
            if (!res) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] })
                return
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error })
                return
            }

            if (!res.data.salesSetup) {
                this.enableBHPH = false
                return
            }

            this.enableBHPH = res.data.salesSetup.enableBHPH
        })
    }

    /**
     * Handle a lot change and query for a new list of sales.
     * @param lot Unique identification of the selected lot
     */
    protected changeLot = (lot: number) => {
        if (this.lotSelected === lot) {
            return
        }

        this.lotSelected = lot || parseInt(localStorage.getItem('lot')) || parseInt(this.store.lotSelected) || 0
        if (!this.lotSelected || (this.lotSelected <= 0)) {
            return
        } 

        this.form.get('lot').setValue(this.lotSelected)
        this.setEnableBHPH()
    }

    /**
     * Handle scroll events of the list container and load 
     * the next page when avilable and the user scrolled 
     * to the bottom.
     * @param e The scroll event that trigggered this handler.
     */
    protected scrolled = (e): void => {
        if (!e || !e.target) {
            return
        }

        if (this.exhausted) {
            return
        }

        if (this.loading) {
            return
        }

        const location = Math.abs(e.target.scrollHeight - e.target.clientHeight - e.target.scrollTop)

        if (location >= SCROLL_BOTTOM_OUT) {
            return
        }

        this.page = this.page + 1
        this.fetch()
    }

    /**
     * 
     * @param field The field that user wants to sort by. 
     */
    protected sort = (field: string): void => {
        if (!field || (field.length <= 0)) {
            return
        }

        if (this.sortables[field]) { // need to toggle the direction?
            switch (this.sortables[field]) {
                case 'ASC':
                    this.sortables[field] = 'DESC'
                    break
                default:
                    this.sortables[field] = 'ASC'
                    break
            }

            this.reset()
            this.fetch()
            return
        }

        this.sortables = {}
        this.sortables[field] = 'ASC'
        this.reset()
        this.fetch()
    }

    protected sortedAsc = (field: string): Boolean => {
        if (!field || (field.length <= 0) || !this.sortables) {
            return false
        }

        if (!this.sortables[field]) {
            return false
        }

        return (this.sortables[field].toUpperCase() === 'ASC')
    }

    protected sortedDesc = (field: string): Boolean => {
        if (!field || (field.length <= 0) || !this.sortables) {
            return false
        }

        if (!this.sortables[field]) {
            return false
        }

        return (this.sortables[field].toUpperCase() === 'DESC')
    }

    /**
     * Query for a list of sales.
     * @param lot Unique identification of the selected lot
     */
    protected fetch = async () => {
        if (this.loading) { // already getting next page
            return
        }
  
        if (this.exhausted) { // currently showing all available records?
            return
        }
  
        this.loading = true
  
        let lotParam = ''
        const lot = parseInt(this.form.get('lot').value)
        if (lot && (!isNaN(lot)) && (lot > 0)) {
            lotParam = `&lotId=${lot}`
        }
  
        let inquiryParam = ''
        if (this.inquiry && (this.inquiry.length > 0)) {
            inquiryParam = `&q=${encodeURIComponent(this.inquiry)}`
        }

        let statusParam = ''
        const status = parseInt(this.form.get('status').value)
        if (status && (!isNaN(status)) && (status > 0)) {
            statusParam = `&status=${status}`
        }

        let typeParam = ''
        const types = this.form.get('type').value
        if (types && (types.length > 0)) {
            typeParam = `&types=${encodeURIComponent(types)}`
        }

        let stageParam = `&stage=2` // 2 = sale
        if (this.isQuote) {
            stageParam = `&stage=1` // 1 = quote
        }

        let spanParam = ''
        const spanKey = this.form.get('span').value
        if (spanKey && (spanKey.length > 0)) {
            switch (spanKey) {
                case 'custom':
                    const from = this.form.get('from').value
                    const to = this.form.get('to').value
                    spanParam = `&from=${from}&to=${to}`
                    break
                default:
                    spanParam = `&span=${spanKey}`
                    break
            }
        }
  
        let sortParam = ''
        if (this.sortables && (Object.keys(this.sortables).length > 0)) {
            const sorts = []
            for (const key of Object.keys(this.sortables)) {
                let direction = ''
                if (this.sortables[key] && this.sortables[key].length > 0) {
                    direction = `|${this.sortables[key]}`
                }
  
                sorts.push(`${key}${direction}`)
            }
  
            sortParam = `&sort=${sorts.join(',')}`
        }

        this.loading = true
        await this.master.getAsync(`sales?page=${this.page}${inquiryParam}${sortParam}${lotParam}${stageParam}${statusParam}${typeParam}${spanParam}`).then(res => {
            this.loading = false

            if (!res || !res.data) {
                return
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error ? res.data.error : this.words[this.language]['apiNoResponse'] });
                return
            }

            this.records = res.data.records
            this.displayed = Math.min(this.records, (res.data.page * res.data.ipp))
            this.exhausted = (res.data.next === null)

            if (this.page > 1) {
                this.sales = this.sales.concat(res.data.sales)
                return
            }

            this.sales = res.data.sales
        })
    }

    //
    protected preselect = (object) => {
        this.preselected = { ...object }
    }

    /**
     * @param index 
     */
    protected toggleCell = (index?: number): void => {
        if ((index !== undefined) && (index >= 0) && (index <= this.headers.length - 1)) {
            this.headers[index]['view'] = !this.headers[index]['view']
        }
    }

     // save View
  protected saveView = (): void => {
    let config = JSON.stringify(this.headers)
    this.master.put(`user/${this.userId}`, { users: { salesTable: config, id: this.userId } }, res => {
      if (!res || !res.data) {
        this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] });
        return
      }

      if (res.status !== 200) {
        this.ms.sendMessage("alert", { type: "danger", text: res.data.error });
        return
      }
      document.getElementById('btn-customizeColumns-close').click()
    })
  }

    // move the position of columns in the table
    protected drop(event: CdkDragDrop<string[]>) {

        if (event.previousContainer === event.container) {
            moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
        } else {
            transferArrayItem(event.previousContainer.data,
                event.container.data,
                event.previousIndex,
                event.currentIndex);
        }
        this.toggleCell()
        // this.changePosition(event.currentIndex, event.previousIndex)
    }

    // ? pagitation
    // return true if two words are equal, else return false
    protected compareLower = (word1: string, word2: string): boolean => {
        return word1 != null && word2 != null ? word1.toLowerCase().indexOf(word2.toLowerCase()) >= 0 : false
    }

    // ? create data
    //  create a new vehicle
    protected createSale = (type: number): void => {
        if (this.security.notPermitted('sales', 'sale detail', 'add')) {
            return
        }
        
        this.router.navigate(
            [`/${this.path}/add`],
            {
                queryParams: {
                    type: type,
                }
            }
        );
    }

    // View the modify sale detail page
    protected modifySale = (id: string): void => {
        this.router.navigate([`/${this.path}/${id}`]);
    }

    //  select a vehicle
    protected selectSale = (sale: Object): void => {
        this.preselectedObject = { ...sale }
    }

    // delete the contact selected
    protected delete = (): void => {
        let tradeIn = this.preselectedObject['data'] ? this.preselectedObject['data'].saleVehicles.filter(el => el['saleVehicleTransactionTypeId'] == 2) : 0

        if (tradeIn.length > 0) {
            this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['The selected sale cannot be deleted because it has an associated trade-in'] });
            (document.getElementById('btn-close-modal-delete') as HTMLButtonElement).click();
            return
        }

        if (this.preselectedObject['data'] && this.preselectedObject['data'].saleVehicles.length > 0) {
            for (let index = 0; index < this.sales.length; index++) {
                if (this.sales[index]['data'].saleVehicles.some(el => el['inventory'] && el.inventory['originId'] && el.inventory['originId'] == this.preselectedObject['data']['sale'].saleVehicles[0].id)) {
                    this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['The selected sale cannot be deleted because it has an associated trade-in'] });
                    (document.getElementById('btn-close-modal-delete') as HTMLButtonElement).click();
                    return
                }
            }
        }

        this.loading = true;
        this.master.discard(`sales/${this.preselectedObject['id']}`, res => {
            this.loading = false

            if (!res) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] });
                return
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error });
                return
            }

            this.changeLot(this.lotSelected)
            this.ms.sendMessage("alert", { type: "success", text: res.data.message });
            
            (document.getElementById('btn-close-modal-delete') as HTMLButtonElement).click();
            this.reset()
            this.fetch()
        })
    }

    // todo: listen to output of navbar
    protected listenerNav = (e): void => {
        switch (e.message) {
            case 'changeLot':
                this.changeLot(e.lot)
                break;
            case 'changeLanguage':
                this.language = e.value
                break;
            case 'setPermissions':
                this.determineLots()
                this.reset()
                this.fetch()
                break
            default:
                break;
        }
    }

    /**
     * Check session storage for stored filters and apply them if found.
     */
    private readFilters = () => {
        if (!sessionStorage) {
            return
        }

        const qFilter = sessionStorage.getItem(this.FILTER_INQUIRY_KEY)
        if (qFilter && (qFilter.length > 0)) {
            this.form.get('inquiry').setValue(qFilter, {emitEvent: false})
            this.inquiry = qFilter
        }

        const lotFilter = sessionStorage.getItem(this.FILTER_LOT_KEY)
        if (lotFilter && (lotFilter.length > 0)) {
            this.form.get('lot').setValue(lotFilter.toString(), {emitEvent: false})
        }

        const typeFilter = sessionStorage.getItem(this.FILTER_TYPE_KEY)
        if (typeFilter && (typeFilter.length > 0)) {
            this.form.get('type').setValue(typeFilter.toString(), {emitEvent: false})
        }

        const statusFilter = parseInt(sessionStorage.getItem(this.FILTER_STATUS_KEY))
        if (statusFilter && (!isNaN(statusFilter)) && (statusFilter > 0)) {
            this.form.get('status').setValue(statusFilter.toString(), {emitEvent: false})
        }

        const spanFilter = sessionStorage.getItem(this.FILTER_SPAN_KEY)
        if (spanFilter && (spanFilter.length > 0)) {
            if (spanFilter.startsWith('custom')) {
                const items = spanFilter.split(';')
                if (!items || (items.length !== 3)) {
                    return
                }

                this.form.get('span').setValue(items[0], {emitEvent: false})
                this.form.get('from').setValue(items[1], {emitEvent: false})
                this.form.get('to').setValue(items[2], {emitEvent: false})    

                return
            }

            this.form.get('span').setValue(spanFilter, {emitEvent: false})
        }
    }

    /**
     * Save the existing list fitlers to session storage for later retrieval.
     */
    private writeFilters = () => {
        if (!sessionStorage) {
            return
        }

        const qFilter = this.form.get('inquiry').value
        if (!qFilter || (qFilter.length <= 0)) {
            sessionStorage.removeItem(this.FILTER_INQUIRY_KEY)
        }
        else {
            sessionStorage.setItem(this.FILTER_INQUIRY_KEY, qFilter)
        }

        const lotFilter = this.form.get('lot').value
        if (!lotFilter || (lotFilter.length <= 0)) {
            sessionStorage.removeItem(this.FILTER_LOT_KEY)
        }
        else {
            sessionStorage.setItem(this.FILTER_LOT_KEY, lotFilter)
        }

        const typeFilter = this.form.get('type').value
        if (!typeFilter || (typeFilter.length <= 0)) {
            sessionStorage.removeItem(this.FILTER_TYPE_KEY)
        }
        else {
            sessionStorage.setItem(this.FILTER_TYPE_KEY, typeFilter)
        }

        const statusFilter = parseInt(this.form.get('status').value)
        if (!statusFilter || (isNaN(statusFilter)) || (statusFilter <= 0)) {
            sessionStorage.removeItem(this.FILTER_STATUS_KEY)
        }
        else {
            sessionStorage.setItem(this.FILTER_STATUS_KEY, statusFilter.toString())
        }

        const spanFilter = this.form.get('span').value
        if (!spanFilter || (spanFilter.length <= 0)) {
            sessionStorage.removeItem(this.FILTER_SPAN_KEY)
        }
        else {
            let fromTo = ''
            if (spanFilter === 'custom') {
                const from = this.form.get('from').value
                const to = this.form.get('to').value
                fromTo = `;${from};${to}`
            }

            sessionStorage.setItem(this.FILTER_SPAN_KEY, `${spanFilter}${fromTo}`)
        }
    }

    /**
     * Handle date span choices by the user.
     * @param choice 
     */
    protected spanned = (choice: Object) => {
        if (!choice || (choice['key'] === undefined) || (choice['key'] === null)) {
            return
        }

        if (choice['key'] === 'custom') {
            const from = choice['from']
            const to = choice['to']
            if (!from || !to) {
                return
            }

            this.form.get('from').setValue(from, {emitEvent: false})
            this.form.get('to').setValue(to, {emitEvent: false})    
        }

        this.form.get('span').setValue(choice['key'])
    }

    /**
     * Check the user store to get a lot list.
     */
    private determineLots = (): void => {
        this.lots = []
        if (!this.store || !this.store.userAccount || 
            !this.store.userAccount['user'] || 
            !this.store.userAccount['user'].lots) {
            return
        }

        const userLots = this.store.userAccount['user'].lots
        for (const lot of userLots) {
            this.lots.push({
                ...new Lot(),
                id: lot.id,
                name: lot.contact.name
            })
        }
    }

    /**
     * 
     * @param expires 
     * @returns 
     */
    protected expiryToClass = (expires: Date): string => {
        if (!expires) {
            return ''
        }

        return ago(expires.toString())
    }

}
